import React, { useState, useRef, useEffect } from 'react';
import { formatMinutes, timeToMinutes } from './timeUtils';
import { resolveSlotLabel } from './labelResolver';
import { hashColor } from './color';

/**
 * Chip that collapses a run of sub-8px-wide slots into a single "⋯ N" pill.
 * Hover shows a list tooltip; click pins a popover so the admin can read the
 * contents without hunting the cursor.
 *
 * Positioned with CSS percentages so it follows viewport resize automatically.
 * `minWidth: 26px` keeps the chip readable even when the original run was
 * only a few pixels wide.
 */
const AggregationChip = ({
  slots,
  contentMap,
  leftPct,
  widthPct,
  dimmed,
  highlighted,
}) => {
  const [open, setOpen] = useState(false);
  const [hover, setHover] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  return (
    <div
      ref={ref}
      style={{
        position: 'absolute',
        left: `${leftPct * 100}%`,
        width: `${widthPct * 100}%`,
        minWidth: '26px',
        top: '4px',
        bottom: '4px',
        backgroundColor: 'var(--gray-900, #1c1917)',
        borderRadius: '4px',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#fff',
        fontSize: '10px',
        fontWeight: 700,
        letterSpacing: '0.03em',
        opacity: dimmed ? 0.5 : 1,
        outline: highlighted ? '2px solid #fff' : 'none',
        outlineOffset: highlighted ? '1px' : 0,
        zIndex: open ? 3 : 2,
        transition: 'opacity 0.15s ease',
      }}
      onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      title={`${slots.length} plays — click for details`}
    >
      ⋯ {slots.length}

      {(open || hover) && (
        <div
          style={{
            position: 'absolute',
            left: '0',
            top: 'calc(100% + 6px)',
            minWidth: '220px',
            maxWidth: '320px',
            maxHeight: '260px',
            overflowY: 'auto',
            backgroundColor: 'var(--gray-900, #1c1917)',
            color: '#fff',
            borderRadius: '8px',
            padding: '10px 12px',
            fontSize: '11px',
            fontWeight: 500,
            letterSpacing: 'normal',
            boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
            zIndex: 50,
            pointerEvents: open ? 'auto' : 'none',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={{
            fontSize: '10px', fontWeight: 700, letterSpacing: '0.06em',
            textTransform: 'uppercase', color: 'rgba(255,255,255,0.6)',
            marginBottom: '6px',
          }}>
            {slots.length} plays
          </div>
          {slots.map((s) => {
            const { label } = resolveSlotLabel(s, contentMap);
            const c = hashColor(s.contentId, label);
            const startMin = timeToMinutes(s.startTime);
            const endMin = timeToMinutes(s.endTime);
            return (
              <div
                key={s.scheduleId || s.id || `${s.contentId}-${s.startTime}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '4px 0',
                  borderBottom: '1px solid rgba(255,255,255,0.08)',
                }}
              >
                <span style={{
                  width: '8px', height: '8px', borderRadius: '2px',
                  backgroundColor: c.bg, flexShrink: 0,
                }} />
                <span style={{
                  flex: 1, minWidth: 0,
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                }}>{label}</span>
                <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '10px' }}>
                  {formatMinutes(startMin)} – {formatMinutes(endMin)}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AggregationChip;
